import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import { getPanchkarmaUsersAction, deletePanchkarmaUserAction } from "../../../store/actions/PanchkarmaActions/UserAction";
import Loader from "../Common/Loader";
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService";

const PanchkarmaUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Static sample data
  // const staticUsers = [
  //   { admin_id: 1, admin_username: "John Doe", role_name: "Admin", admin_email: "john@example.com", admin_status: "Active" },
  //   { admin_id: 2, admin_username: "Jane Smith", role_name: "Telecaller", admin_email: "jane@example.com", admin_status: "Inactive" },
  //   { admin_id: 3, admin_username: "Dr. Raj", role_name: "Service Caller", admin_email: "raj@example.com", admin_status: "Active" },
  // ];
  const users = useSelector((state) => {
    return state.panchkarmaUser.panchkarmaUsers || [];
  });

  // const users = useSelector((state) => {
  //   return state.adminUser.adminUsers || [];
  // });

  // const users = useSelector((state) => {
  //   return state.adminUser.adminUsers || [];
  // });
  const numRows = useSelector((state) => state.panchkarmaUser?.num_rows || 0);
  // const [users, setUsers] = useState([]);
  // const [numRows, setNumRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchQueries, setSearchQueries] = useState({
    admin_username: "",
    admin_email: "",
    admin_status: "",
    role_name: "",
  });

  const [sortField, setSortField] = useState("admin_username");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  useEffect(() => {
    const fetchPanchkarmaUsers = async () => {
      try {
        setLoading(true);
        setError("");

        if (location.state?.currentPage !== undefined) {
          setCurrentPage(location.state.currentPage);
        }
        if (location.state?.searchQueries) {
          setSearchQueries(location.state.searchQueries);
        }
        if (location.state?.sortField) {
          setSortField(location.state.sortField);
        }
        if (location.state?.sortOrder) {
          setSortOrder(location.state.sortOrder);
        }

        await fetchPanchkarmaUsersFromServer();
      } catch (error) {
        console.error("Error fetching panchkarma users:", error);
        setError("Failed to load panchkarma users. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPanchkarmaUsers();
  }, [dispatch]);

  const fetchPanchkarmaUsersFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = 0
  ) => {
    try {
      await dispatch(
        getPanchkarmaUsersAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order, { is_panchkarma: "Yes" } )
      );
    } catch (error) {
      console.error("Error fetching panchkarma users:", error);
      setError("Failed to load panchkarma users.");
    }
  };

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries first, then set the current field
    const clearedQueries = {
      admin_username: "",
      admin_email: "",
      admin_status: "",
      role_name: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0); // Reset to first page on search

    // Track active search
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
    fetchPanchkarmaUsersFromServer(field, keyword, sortField, sortOrder, true, 0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPanchkarmaUsersFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      page
    );
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    fetchPanchkarmaUsersFromServer(activeSearchField, activeSearchKeyword, field, order, true, currentPage);
  };

  const handleEdit = (user) => {
    if (user.role_name === "Therapist") {
      swal("Not Allowed", "Editing Therapist user is not permitted.", "warning");
      return;
    }
    navigate(`/panchkarma-edit-user/${user.admin_id}`, {
      state: { user, returnTo: { currentPage, searchQueries, sortField, sortOrder } },
    });
  };

  const handleDelete = (user) => {
    swal({
      title: "Are you sure?",
      text: `Delete user "${user.admin_username}"? This action cannot be undone!`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        const admin_id = user.admin_id || user.id || user._id;

        if (!admin_id) {
          showErrorAlert("Invalid user ID", "error");
          return;
        }

        try {
          setLoading(true);
          await dispatch(deletePanchkarmaUserAction({ admin_id }));
          handleSuccess("User has been deleted successfully!");

          // Refresh the current page after deletion
          fetchPanchkarmaUsersFromServer(
            activeSearchField,
            activeSearchKeyword,
            sortField,
            sortOrder,
            true,
            currentPage
          );
        } catch (err) {
          console.error("Delete failed:", err);
          showErrorAlert("Failed to delete user. Please try again.", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const paginatedData = users;
  const totalPages = Math.ceil(numRows / rowsPerPage);


  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <button
            className="btn btn-primary btn-rounded"
            onClick={() =>
              navigate("/panchkarma-add-user", {
                state: {
                  returnTo: {
                    currentPage,
                    searchQueries,
                    sortField,
                    sortOrder,
                  },
                },
              })
            }
          >
            + Add User
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  {/* Responsive Table Wrapper */}
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("admin_username")} style={{ cursor: "pointer" }}>
                            User Name{" "}
                            {sortField === "admin_username" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.admin_username}
                              onChange={(e) => handleSearch(e, "admin_username")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search User"
                            />
                          </th>
                          <th onClick={() => handleSort("role_name")} style={{ cursor: "pointer" }}>
                            Role{" "}
                            {sortField === "role_name" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.role_name || ""}
                              onChange={(e) => handleSearch(e, "role_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Role"
                            />
                          </th>
                          <th onClick={() => handleSort("admin_email")} style={{ cursor: "pointer" }}>
                            Email{" "}
                            {sortField === "admin_email" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.admin_email}
                              onChange={(e) => handleSearch(e, "admin_email")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Email"
                            />
                          </th>
                          <th onClick={() => handleSort("admin_status")} style={{ cursor: "pointer" }}>
                            Status{" "}
                            {sortField === "admin_status" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.admin_status}
                              onChange={(e) => handleSearch(e, "admin_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((user) => (
                          <tr key={user.admin_id}>
                            <td>{user.admin_username}</td>
                            <td>{user.role_name || "-"}</td>
                            <td>{user.admin_email}</td>
                            <td>
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${
                                  user.admin_status.toLowerCase() === "active"
                                    ? "bg-success"
                                    : "bg-danger"
                                }`}
                              >
                                {user.admin_status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex justify-content-around">
                                <span
                                  className="edit me-3"
                                  onClick={() => handleEdit(user)}
                                  style={{
                                    cursor: user.role_name === "Therapist" ? "not-allowed" : "pointer",
                                    opacity: user.role_name === "Therapist" ? 0.5 : 1,
                                  }}
                                  title={
                                    user.role_name === "Therapist"
                                      ? "Editing Therapist user is not allowed"
                                      : "Edit user"
                                  }
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => {
                                    if (user.role_name === "Therapist") {
                                      swal(
                                        "Not Allowed",
                                        "Deleting Therapist user is not permitted.",
                                        "warning"
                                      );
                                      return;
                                    }
                                    handleDelete(user);
                                  }}
                                  style={{
                                    cursor: user.role_name === "Therapist" ? "not-allowed" : "pointer",
                                    opacity: user.role_name === "Therapist" ? 0.5 : 1,
                                  }}
                                  title={
                                    user.role_name === "Therapist"
                                      ? "Deleting Therapist user is not allowed"
                                      : "Delete user"
                                  }
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {users.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {users?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                        }
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PanchkarmaUsers;
